import { useState, useRef, useEffect } from "react";
import CameraAltIcon from "@mui/icons-material/CameraAlt";
import GraphicEqIcon from "@mui/icons-material/GraphicEq";
import SaveAltIcon from "@mui/icons-material/SaveAlt";
import PlayCircleOutlineIcon from "@mui/icons-material/PlayCircleOutline";
import PauseCircleOutlineIcon from "@mui/icons-material/PauseCircleOutline";
import { getGlobalRenderingEngine } from "../context/renderingEngine";
import { Enums } from "@cornerstonejs/core";

// =========================
// SCREENSHOT
// =========================
export function ScreenshotTool({ viewportId }) {
  const handleScreenshot = async () => {
    const re = await getGlobalRenderingEngine();
    const viewport = re?.getViewport(viewportId);
    if (!viewport) return;

    const canvas = viewport.element.querySelector("canvas");
    if (!canvas) return;

    const url = canvas.toDataURL("image/png");
    const link = document.createElement("a");
    link.href = url;
    link.download = `screenshot_${viewportId}.png`;
    link.click();
  };

  return (
    <div className="overlayToolBtn" onClick={handleScreenshot} title="Screenshot">
      <CameraAltIcon fontSize="small" />
    </div>
  );
}

// =========================
// DOWNLOAD IMAGE
// =========================
export function DownloadTool({ viewportId }) {
  const handleDownload = async () => {
    const re = await getGlobalRenderingEngine();
    const viewport = re?.getViewport(viewportId);
    if (!viewport) return;

    const canvas = viewport.element.querySelector("canvas");
    if (!canvas) return;

    const url = canvas.toDataURL("image/png");
    const link = document.createElement("a");
    link.href = url;
    link.download = `dicom_${viewportId}.png`;
    link.click();
  };

  return (
    <div className="overlayToolBtn" onClick={handleDownload} title="Download">
      <SaveAltIcon fontSize="small" />
    </div>
  );
}

// =========================
// WAVEFORM / ECG
// =========================
export function WaveformTool({ viewportId }) {
  const [show, setShow] = useState(false);

  return (
    <>
      <div className="overlayToolBtn" onClick={() => setShow((p) => !p)} title="Waveform">
        <GraphicEqIcon fontSize="small" />
      </div>
      {show && (
        <div className="waveformOverlay">
          <div className="waveformHeader">
            <span>ECG / Waveform</span>
            <span style={{ cursor: "pointer" }} onClick={() => setShow(false)}>✕</span>
          </div>
          <div className="waveformBody">
            <svg width="100%" height="60">
              <polyline
                points="0,30 20,30 30,10 40,50 50,30 70,30 80,10 90,50 100,30 120,30 130,10 140,50 150,30 200,30"
                fill="none"
                stroke="#00ff00"
                strokeWidth="1.5"
              />
            </svg>
            <div style={{ color: "#aaa", fontSize: "11px", textAlign: "center" }}>
              ECG data not available for this series
            </div>
          </div>
        </div>
      )}
    </>
  );
}

// =========================
// CINE PLAY
// =========================

export function CineTool({ viewportId, totalSlices }) {
  const [isPlaying, setIsPlaying] = useState(false);
  const intervalRef = useRef(null);
  const directionRef = useRef(1);

  //  MPR slice calculation (IMPORTANT)
  const getCurrentSliceFromCamera = (viewport, totalSlices) => {
  const imageData = viewport.getImageData();
  if (!imageData) return 0;

  const { origin, spacing } = imageData;
  const focal = viewport.getCamera().focalPoint;

  const slice = Math.round((focal[2] - origin[2]) / spacing[2]);

  return Math.max(0, Math.min(totalSlices - 1, slice));
};

  // MPR movement with LOOP
  const moveSliceMPR = (viewport, direction) => {
  const camera = viewport.getCamera();
  const normal = camera.viewPlaneNormal;

  const currentSlice = getCurrentSliceFromCamera(viewport, totalSlices);

  let newSlice = currentSlice + direction;

  // 🔥 LOOP LOGIC (IMPORTANT)
  if (newSlice >= totalSlices) {
    newSlice = 0;
  }
  if (newSlice < 0) {
    newSlice = totalSlices - 1;
  }

  const step = newSlice - currentSlice;

  viewport.setCamera({
    position: camera.position.map((p, i) => p + normal[i] * step),
    focalPoint: camera.focalPoint.map((p, i) => p + normal[i] * step),
  });

  viewport.render();
};
  // changeSlice (STACK + MPR LOOP)
  const changeSlice = async (viewport, direction) => {
  try {
    // ✅ MPR
    if (viewport.type === Enums.ViewportType.ORTHOGRAPHIC) {
      moveSliceMPR(viewport, direction);
      return true; // never stop
    }

    // ✅ STACK
    const currentIndex = viewport.getCurrentImageIdIndex();
    let newIndex = currentIndex + direction;

    if (newIndex >= totalSlices) newIndex = 0;
    if (newIndex < 0) newIndex = totalSlices - 1;

    viewport.setImageIdIndex(newIndex);
    viewport.render();

    return true;
  } catch (e) {
    console.error(e);
    return false;
  }
};
  //  STOP
  const stopCine = () => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    setIsPlaying(false);
  };

  //  START
  const startCine = async () => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }

    setIsPlaying(true);

    intervalRef.current = setInterval(async () => {
      const re = await getGlobalRenderingEngine();
      const viewport = re?.getViewport(viewportId);

      if (!viewport) return;

      await changeSlice(viewport, directionRef.current);
    }, 60); // smooth cine
  };

  //  TOGGLE
  const toggleCine = () => {
    if (intervalRef.current) {
      stopCine();
    } else {
      directionRef.current = 1;
      startCine();
    }
  };

  useEffect(() => {
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, []);

  return (
    <div className="overlayToolBtn" onClick={toggleCine} title="Cine Play">
      {intervalRef.current ? (
        <PauseCircleOutlineIcon fontSize="small" />
      ) : (
        <PlayCircleOutlineIcon fontSize="small" />
      )}
    </div>
  );
}
// =========================
// COMBINED OVERLAY
// =========================
export function PanelOverlayTools({ viewportId, totalSlices }) {
  return (
    <div className="panelOverlayTools">
      <ScreenshotTool viewportId={viewportId} />
      <WaveformTool viewportId={viewportId} />
      <DownloadTool viewportId={viewportId} />
      <CineTool viewportId={viewportId} totalSlices={totalSlices} />
    </div>
  );
}